
import React, { useRef, useState } from 'react';
import { Box, Flex, TextField } from '@radix-ui/themes';
import * as Label from '@radix-ui/react-label';
import * as Select from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Footer } from '../../../layout/footer/Footer';
import useLanguage from '../../../contexts/language/LanguageContext';
import { Page, TransitionUrl, TranslationPath } from '../../../contexts/kioskConfiguration/type';
import { RootState } from '../../../store/store';
import { getTranslationByPath } from '../../../utils/translations';
import { MainContent } from '../../../layout/content/MainContent';
import '../announcementForm/style.css';
import { setTransportationForm } from '../../../store/slices/transportation/transportationSlice';
import { checkAuthCode } from '../../../apollo/mutations/checkAuthCode';
import { addNotification } from '../../../store/slices/notification/notificationSlice';

export type BozpDataTemplateType = {
  transitionUrl: TransitionUrl
  driverNameInputLabel: TranslationPath
  driverNameInputPlaceholder: TranslationPath
  phoneNumberInputLabel: TranslationPath
  phoneNumberInputPlaceholder: TranslationPath
  pinInputLabel: TranslationPath
  pinInputPlaceholder: TranslationPath
  registrationNumberInputLabel: TranslationPath
  registrationNumberInputPlaceholder: TranslationPath
  documentTypeInputLabel: TranslationPath
  documentTypeInputPlaceholder: TranslationPath
  documentTypeIdCardLabel: TranslationPath
  documentTypePassportLabel: TranslationPath
  documentNumberInputLabel: TranslationPath
  documentNumberInputPlaceholder: TranslationPath
  trainingDatesInputLabel: TranslationPath
  trainingDatesInputPlaceholder: TranslationPath
  invalidDriverNameText: TranslationPath
  invalidAuthCodeText: TranslationPath
  registrationFailedText: TranslationPath
}

type Props = {
  page: Page<BozpDataTemplateType>
}

const formatDateTime = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${day}.${month}.${year} ${hours}:${minutes}`
}

const getTodayRange = (): string => {
  const from = new Date()
  from.setHours(0, 0, 0, 0)
  const to = new Date()
  to.setHours(23, 59, 59, 999)
  return `${formatDateTime(from)} - ${formatDateTime(to)}`
}

const getTrainingRangeFromTransportation = (
  scheduleFrom?: string,
  scheduleTo?: string,
): string => {
  if (!scheduleFrom || !scheduleTo) {
    return getTodayRange()
  }

  const from = new Date(scheduleFrom)
  const to = new Date(scheduleTo)

  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return getTodayRange()
  }

  from.setHours(0, 0, 0, 0)
  to.setHours(23, 59, 59, 999)

  return `${formatDateTime(from)} - ${formatDateTime(to)}`
}

export const BozpDataTemplate = (props: Props): React.ReactElement => {
  const language = useLanguage()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [pin, setPin] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  const fetchedTransportation = useSelector((state: RootState) => state.transportation.fetchedTransportation)
  const transportationForm = useSelector((state: RootState) => state.transportation.transportationForm)

  const driverName = transportationForm.driverName ?? fetchedTransportation?.driverName ?? ''
  const phoneNumber = transportationForm.phoneNumber || fetchedTransportation?.phoneNumber || ''
  const registrationNumber = fetchedTransportation?.registrationNumber || transportationForm.registrationNumber || ''
  const trainingRange = getTrainingRangeFromTransportation(
    fetchedTransportation?.scheduleFrom,
    fetchedTransportation?.scheduleTo,
  )
  const idCardNumber = transportationForm.idCardNumber
    || (fetchedTransportation?.driverIdType === 'idCard' ? fetchedTransportation?.driverIdNo : '')
    || ''
  const passportNumber = transportationForm.passportNumber
    || (fetchedTransportation?.driverIdType === 'passport' ? fetchedTransportation?.driverIdNo : '')
    || ''
  const documentType = idCardNumber
    ? 'idCard'
    : passportNumber
      ? 'passport'
      : fetchedTransportation?.driverIdType === 'passport'
        ? 'passport'
        : 'idCard'
  const documentNumber = idCardNumber || passportNumber
  const isDriverNamePrefilled = useRef(
    Boolean((transportationForm.driverName ?? fetchedTransportation?.driverName ?? '').trim()),
  )

  const onContinue = async () => {
    if (driverName.trim().length === 0) {
      dispatch(addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidDriverNameText) },
      ))
      return
    }
    if (pin.trim().length === 0) {
      dispatch(addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.invalidAuthCodeText) },
      ))
      return
    }

    try {
      setIsSaving(true)
      const result = await checkAuthCode({
        authCode: pin,
        phoneNumber,
      })
      if (result.data?.checkAuthCode) {
        navigate(props.page.data.transitionUrl)
      } else {
        dispatch(addNotification(
          { type: 'error', title: getTranslationByPath(language.translations, props.page.data.registrationFailedText) },
        ))
      }
    } catch (e) {
      dispatch(addNotification(
        { type: 'error', title: getTranslationByPath(language.translations, props.page.data.registrationFailedText) },
      ))
    }
    setIsSaving(false)
  }

  return (
    <>
      <MainContent>
        <Flex className="flex-col justify-center items-center w-full mt-10">
          <Flex direction="column" gap="1" width="100%" className="items-center">
            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10">
                {getTranslationByPath(language.translations, props.page.data.driverNameInputLabel)}
              </Label.Root>
              <TextField.Root
                disabled={isDriverNamePrefilled.current}
                style={{ height: '50px' }}
                className="mt-3"
                placeholder={getTranslationByPath(language.translations, props.page.data.driverNameInputPlaceholder)}
                value={driverName}
                onChange={(e) => {
                  dispatch(setTransportationForm({
                    ...transportationForm,
                    driverName: e.target.value,
                  }))
                }}
              />
            </Box>

            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10">
                {getTranslationByPath(language.translations, props.page.data.phoneNumberInputLabel)}
              </Label.Root>
              <TextField.Root
                disabled
                style={{ height: '50px' }}
                className="mt-3"
                placeholder={getTranslationByPath(language.translations, props.page.data.phoneNumberInputPlaceholder)}
                value={phoneNumber}
              />
            </Box>

            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10">
                {getTranslationByPath(language.translations, props.page.data.pinInputLabel)}
              </Label.Root>
              <TextField.Root
                style={{ height: '50px' }}
                className="mt-3"
                placeholder={getTranslationByPath(language.translations, props.page.data.pinInputPlaceholder)}
                value={pin}
                onChange={(e) => {
                  setPin(e.target.value)
                  dispatch(setTransportationForm({
                    ...transportationForm,
                    pin: e.target.value,
                  }))
                }}
              />
            </Box>

            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10">
                {getTranslationByPath(language.translations, props.page.data.registrationNumberInputLabel)}
              </Label.Root>
              <TextField.Root
                disabled
                style={{ height: '50px' }}
                className="mt-3"
                placeholder={getTranslationByPath(language.translations, props.page.data.registrationNumberInputPlaceholder)}
                value={registrationNumber}
              />
            </Box>

            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10">
                {getTranslationByPath(language.translations, props.page.data.documentTypeInputLabel)}
              </Label.Root>
              <Select.Root value={documentType} disabled>
                <Select.Trigger className="select mt-3">
                  <Select.Value
                    placeholder={getTranslationByPath(language.translations, props.page.data.documentTypeInputPlaceholder)}
                  />
                  <Select.Icon className="select-icon">
                    <ChevronDownIcon />
                  </Select.Icon>
                </Select.Trigger>
                <Select.Portal>
                  <Select.Content role="dialog" position="popper" className="popover" sideOffset={4}>
                    <Select.Viewport className="SelectViewport">
                      <Select.Item value="idCard" className="item">
                        <Select.ItemText>
                          {getTranslationByPath(language.translations, props.page.data.documentTypeIdCardLabel)}
                        </Select.ItemText>
                        <Select.ItemIndicator className="item-indicator">
                          <CheckIcon />
                        </Select.ItemIndicator>
                      </Select.Item>
                      <Select.Item value="passport" className="item">
                        <Select.ItemText>
                          {getTranslationByPath(language.translations, props.page.data.documentTypePassportLabel)}
                        </Select.ItemText>
                        <Select.ItemIndicator className="item-indicator">
                          <CheckIcon />
                        </Select.ItemIndicator>
                      </Select.Item>
                    </Select.Viewport>
                  </Select.Content>
                </Select.Portal>
              </Select.Root>
            </Box>

            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10">
                {getTranslationByPath(language.translations, props.page.data.documentNumberInputLabel)}
              </Label.Root>
              <TextField.Root
                disabled
                style={{ height: '50px' }}
                className="mt-3"
                placeholder={getTranslationByPath(language.translations, props.page.data.documentNumberInputPlaceholder)}
                value={documentNumber}
              />
            </Box>

            <Box maxWidth="500px" className="w-full mt-5">
              <Label.Root className="LabelRoot text-2xl mb-10">
                {getTranslationByPath(language.translations, props.page.data.trainingDatesInputLabel)}
              </Label.Root>
              <TextField.Root
                disabled
                style={{ height: '50px' }}
                className="mt-3"
                placeholder={getTranslationByPath(language.translations, props.page.data.trainingDatesInputPlaceholder)}
                value={trainingRange}
              />
            </Box>
          </Flex>
        </Flex>
      </MainContent>

      <Footer
        backUrl={props.page.footer?.back}
        onContinue={onContinue}
        disabledContinueBtn={isSaving}
      />
    </>
  )
}
